/**
 * Cliente do motor do discador para o widget ("modo motor").
 *
 * No modo motor a sessão e a mídia do Callix vivem num Chromium headless no servidor
 * (engine/); o navegador do agente só traz microfone e fone. Este cliente fala o
 * protocolo do host (engine/protocol.ts) por WebSocket e negocia o áudio por WebRTC
 * com a página do motor, igual à aba de referência em engine/tab/tab.js:
 *
 *   hello (agente, credencial, sessão) ──► welcome/view ──► engage ──► offer/answer/ICE ──► áudio
 *
 * Credencial: o widget não precisa do token compartilhado do host. A aba se apresenta com o
 * token da sessão do Callix que o próprio widget criou (`sessionToken`, lido do navegador a
 * cada hello) ou entrega uma sessão nova (`session`). O host recusa (`error` com
 * `code: 'unauthorized'`) quando o motor roda uma sessão deste agente com outra credencial:
 * é o caso de outro navegador ou computador; o widget então faz um login novo, que substitui
 * a sessão de lá.
 *
 * É uma "external store": a camada React lê `getState()` e assina `subscribe()`.
 */
import type { AgentView, CallInsight, CallSnapshot, Command, HostToTab, SdkSession, SessionLost, SignalPayload, TabErrorCode, TabToHost, TranscriptSegment, TranscriptStatus } from '../../engine/protocol';

export type WsState = 'idle' | 'connecting' | 'open' | 'closed';

export interface EngineTranscriptState {
  segments: TranscriptSegment[];
  status: TranscriptStatus | null;
  insight: CallInsight | null;
  interims: number;
}

export interface EngineClientState {
  wsState: WsState;
  tabId: string | null;
  view: AgentView | null;
  /** esta aba segura o microfone e o som (as outras são espelhos) */
  engaged: boolean;
  pcState: RTCPeerConnectionState | 'idle';
  /** o navegador bloqueou o som do motor até um toque (autoplay) */
  needsUnlock: boolean;
  micError: string | null;
  lastError: string | null;
  inputMuted: boolean;
  outputMuted: boolean;
  /** 0..100 */
  outputVolume: number;
  transcript: EngineTranscriptState;
  /** a sessão do agente no Callix deixou de valer (login em outro lugar, supervisor...) */
  sessionLost: SessionLost | null;
  /** o host recusou esta aba (motor roda a sessão do agente com outra credencial): só um login novo entra */
  rejected: EngineRejection | null;
}

export interface EngineRejection {
  code: TabErrorCode;
  message: string;
}

/** erro de `waitForView` quando o host recusa a aba: `code` diz o motivo */
export class EngineAccessError extends Error {
  constructor(readonly code: TabErrorCode, message: string) {
    super(message);
    this.name = 'EngineAccessError';
  }
}

export interface EngineClientOptions {
  /** URL do host do motor (https://motor.exemplo.com ou http://127.0.0.1:4700) */
  url: string;
  /** token compartilhado do host (ENGINE_TAB_TOKEN): só para uso administrativo (página tab/, roteiros); o widget não precisa */
  token?: string;
  /** credencial do agente: o token da sessão do Callix guardado no navegador; lido a cada hello, para a reconexão pegar o token novo */
  sessionToken?: () => string | null;
  /** versão do widget, só para o log do host (diagnóstico de clientes antigos ainda em cache) */
  version?: string;
  agentId: string;
  label?: string;
  session?: SdkSession | null;
  /** pedir o áudio assim que conectar, mesmo que outra aba o tenha (padrão: sim) */
  autoEngage?: boolean;
  /** dublês para testes */
  WebSocketImpl?: typeof WebSocket;
  PeerConnectionImpl?: typeof RTCPeerConnection;
  mediaDevices?: Pick<MediaDevices, 'getUserMedia'> | null;
  audioElement?: HTMLAudioElement | null;
  log?(message: string): void;
}

type Listener = () => void;

/** sinal mínimo no formato do SDK do Callix (`get` + `subscribe`), para `useSignal` */
export class EngineSignal<T> {
  private readonly listeners = new Set<(value: T) => void>();

  constructor(private value: T) {}

  get(): T {
    return this.value;
  }

  set(value: T): void {
    if (JSON.stringify(value) === JSON.stringify(this.value)) return;
    this.value = value;
    for (const listener of this.listeners) listener(value);
  }

  subscribe(fn: (value: T) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }
}

export const INITIAL_TRANSCRIPT: EngineTranscriptState = { segments: [], status: null, insight: null, interims: 0 };

const MAX_SEGMENTS = 300;
const RECONNECT_MIN_MS = 500;
const RECONNECT_MAX_MS = 5_000;
/** comandos guardados durante uma queda da conexão: quantos e por quanto tempo ainda valem */
const PENDING_COMMANDS_MAX = 10;
const PENDING_COMMANDS_TTL_MS = 15_000;

export function engineWsUrl(baseUrl: string): string {
  const url = new URL(baseUrl, typeof window !== 'undefined' ? window.location.href : 'http://localhost');
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  url.pathname = `${url.pathname.replace(/\/+$/, '')}/ws`;
  url.search = '';
  url.hash = '';
  return url.toString();
}

export class EngineClient {
  readonly agentId: string;
  /** dados do agente logado (id, nome, login), no formato de sinal do SDK */
  readonly userSignal = new EngineSignal<{ id: number; name: string; login: string; sessionId: number } | undefined>(undefined);
  private state: EngineClientState;
  private readonly listeners = new Set<Listener>();
  private readonly sessionLostListeners = new Set<(lost: SessionLost) => void>();
  private readonly viewWaiters: Array<{ resolve: (view: AgentView) => void; reject: (error: Error) => void; accept?: (view: AgentView) => boolean; timer: ReturnType<typeof setTimeout> }> = [];
  private ws: WebSocket | null = null;
  private reconnectDelay = RECONNECT_MIN_MS;
  /** comandos dados enquanto a conexão com o host estava caída: vão no welcome da reconexão, se ainda forem recentes */
  private pendingCommands: Array<{ command: Command; at: number }> = [];
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private closed = false;
  private session: SdkSession | null;
  private iceServers: RTCIceServer[] = [{ urls: 'stun:stun.l.google.com:19302' }];
  private pc: RTCPeerConnection | null = null;
  private connectionId: string | null = null;
  private remoteSet = false;
  private pendingCandidates: RTCIceCandidateInit[] = [];
  private micStream: MediaStream | null = null;
  private audioSeq = 0;
  private readonly audio: HTMLAudioElement | null;
  private unlockListener: (() => void) | null = null;
  private readonly log: (message: string) => void;

  constructor(private readonly options: EngineClientOptions) {
    this.agentId = options.agentId;
    this.session = options.session ?? null;
    this.log = options.log ?? (() => undefined);
    this.audio = options.audioElement !== undefined ? options.audioElement : createAudioElement();
    this.state = {
      wsState: 'idle',
      tabId: null,
      view: null,
      engaged: false,
      pcState: 'idle',
      needsUnlock: false,
      micError: null,
      lastError: null,
      inputMuted: false,
      outputMuted: false,
      outputVolume: 100,
      transcript: INITIAL_TRANSCRIPT,
      sessionLost: null,
      rejected: null,
    };
  }

  // ------------------------------------------------------------------ store

  getState(): EngineClientState {
    return this.state;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private setState(patch: Partial<EngineClientState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }

  get currentCall(): CallSnapshot | null {
    return this.state.view?.snapshot?.leg.call ?? null;
  }

  // ------------------------------------------------------------------ conexão

  connect(): void {
    if (this.closed) this.closed = false;
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) return;
    const Impl = this.options.WebSocketImpl ?? WebSocket;
    this.setState({ wsState: 'connecting' });
    const ws = new Impl(engineWsUrl(this.options.url));
    this.ws = ws;
    ws.onopen = () => {
      if (this.ws !== ws) return;
      this.reconnectDelay = RECONNECT_MIN_MS;
      this.setState({ wsState: 'open' });
      const sessionToken = !this.session && this.options.sessionToken ? this.options.sessionToken() : null;
      const hello: TabToHost = {
        type: 'hello',
        role: 'tab',
        agentId: this.agentId,
        ...(this.options.token ? { token: this.options.token } : {}),
        ...(this.options.version ? { version: this.options.version } : {}),
        ...(sessionToken ? { sessionToken } : {}),
        label: this.options.label ?? 'Discador',
        ...(this.session ? { session: this.session } : {}),
      };
      this.send(hello);
      this.log('conectado ao host do motor');
    };
    ws.onmessage = (event) => {
      if (this.ws !== ws) return;
      let message: HostToTab;
      try {
        message = JSON.parse(String(event.data)) as HostToTab;
      } catch {
        return;
      }
      this.handle(message);
    };
    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.ws = null;
      const wasEngaged = this.state.engaged;
      this.setState({ wsState: 'closed', tabId: null, engaged: false });
      if (wasEngaged) this.stopAudio();
      if (this.closed) return;
      this.log(`conexão com o motor caiu; nova tentativa em ${this.reconnectDelay} ms`);
      this.reconnectTimer = setTimeout(() => {
        this.reconnectTimer = null;
        this.connect();
      }, this.reconnectDelay);
      this.reconnectDelay = Math.min(this.reconnectDelay * 2, RECONNECT_MAX_MS);
    };
    ws.onerror = () => undefined;
  }

  /** fecha de vez (sem reconectar) e solta o áudio */
  close(): void {
    this.closed = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.stopAudio();
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      ws.onclose = null;
      ws.close();
    }
    for (const waiter of this.viewWaiters.splice(0)) clearTimeout(waiter.timer);
    this.setState({ wsState: 'closed', tabId: null, engaged: false });
  }

  /**
   * A view atual, ou a próxima que o host mandar (welcome/view). Com `accept`, espera uma
   * view que passe no filtro (ex.: motor subindo depois de entregar a sessão). Rejeita com
   * `EngineAccessError` se o host recusou esta aba (`rejected`).
   */
  waitForView(timeoutMs = 15_000, accept?: (view: AgentView) => boolean, options?: { fresh?: boolean }): Promise<AgentView> {
    const rejected = this.state.rejected;
    if (rejected) return Promise.reject(new EngineAccessError(rejected.code, rejected.message));
    // `fresh`: ignora a view já recebida (ex.: logo depois de entregar a sessão, a view antiga ainda traz a queda anterior)
    if (!options?.fresh && this.state.view && this.state.tabId && (!accept || accept(this.state.view))) return Promise.resolve(this.state.view);
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        const index = this.viewWaiters.findIndex((w) => w.timer === timer);
        if (index >= 0) this.viewWaiters.splice(index, 1);
        reject(new Error('o motor não respondeu'));
      }, timeoutMs);
      this.viewWaiters.push({ resolve, reject, accept, timer });
    });
  }

  private rejectWaiters(error: Error): void {
    for (const waiter of this.viewWaiters.splice(0)) {
      clearTimeout(waiter.timer);
      waiter.reject(error);
    }
  }

  private send(message: TabToHost): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(message));
  }

  private handle(message: HostToTab): void {
    switch (message.type) {
      case 'welcome': {
        if (Array.isArray(message.iceServers) && message.iceServers.length > 0) this.iceServers = message.iceServers;
        this.setState({ tabId: message.tabId, rejected: null });
        this.flushPendingCommands();
        this.applyView(message.view);
        this.log(`sou a aba ${message.tabId}`);
        if (this.options.autoEngage !== false && message.view.engagedTabId !== message.tabId) this.engage();
        break;
      }
      case 'view':
        this.applyView(message.view);
        break;
      case 'engage':
        this.setState({ engaged: message.engaged });
        if (message.engaged) void this.startAudio();
        else this.stopAudio();
        break;
      case 'signal':
        void this.onSignal(message.payload);
        break;
      case 'error':
        if (message.code === 'unauthorized') {
          // o motor roda a sessão deste agente com outra credencial (outro navegador/computador):
          // só um login novo entra; até lá, tenta de novo devagar (o token guardado pode mudar)
          this.setState({ lastError: message.message, rejected: { code: message.code, message: message.message } });
          // a sessão que esta aba entregou (se entregou) morreu: não a reapresentar ao reconectar
          this.session = null;
          this.reconnectDelay = RECONNECT_MAX_MS;
          this.log(`acesso recusado pelo host: ${message.message}`);
          this.rejectWaiters(new EngineAccessError(message.code, message.message));
          break;
        }
        if (message.code === 'session-changed') {
          // a sessão desta aba foi substituída: ao reconectar, só o token guardado no navegador vale
          this.session = null;
          this.log('a sessão deste agente foi trocada por um login novo; reconectando com a credencial atual');
          break;
        }
        this.setState({ lastError: message.message });
        this.log(`erro do host: ${message.message}`);
        break;
      case 'transcript':
        this.pushSegment(message.segment);
        break;
      case 'transcript-sync':
        this.setState({ transcript: { segments: message.segments ?? [], status: message.status ?? null, insight: message.insight ?? null, interims: 0 } });
        break;
      case 'insight': {
        const current = this.state.transcript.insight;
        if (!current || message.insight.revision >= current.revision || message.insight.callId !== current.callId) {
          this.setState({ transcript: { ...this.state.transcript, insight: message.insight } });
        }
        break;
      }
      case 'transcript-status':
        this.onTranscriptStatus(message.status);
        break;
    }
  }

  private applyView(view: AgentView): void {
    const previous = this.state.view;
    const lost = view.sessionLost ?? null;
    const newlyLost = Boolean(lost) && !previous?.sessionLost;
    if (newlyLost) {
      // a sessão que esta aba entregou já não vale: não mandar de novo ao reconectar
      this.session = null;
      this.log(`o Callix derrubou a sessão: ${lost?.message ?? ''}`);
    }
    const user = view.snapshot?.leg.user;
    this.userSignal.set(user ? { ...user, sessionId: 0 } : undefined);
    const call = view.snapshot?.leg.call;
    const patch: Partial<EngineClientState> = { view, sessionLost: lost ? { originator: lost.originator, message: lost.message } : null };
    if (call && typeof call.muted === 'boolean' && call.muted !== this.state.inputMuted && call.state !== 'ended') patch.inputMuted = call.muted;
    this.setState(patch);
    for (const waiter of this.viewWaiters.splice(0)) {
      if (waiter.accept && !waiter.accept(view)) {
        this.viewWaiters.push(waiter);
        continue;
      }
      clearTimeout(waiter.timer);
      waiter.resolve(view);
    }
    if (newlyLost && lost) for (const listener of this.sessionLostListeners) listener(lost);
  }

  onSessionLost(listener: (lost: SessionLost) => void): () => void {
    this.sessionLostListeners.add(listener);
    return () => {
      this.sessionLostListeners.delete(listener);
    };
  }

  // ------------------------------------------------------------------ sessão e comandos

  /**
   * Modo sdk: o widget fez login no back-end e entrega a sessão ao host. Com a conexão
   * aberta manda `session`; recusada ou caída, reconecta agora apresentando a sessão no hello
   * (é assim que um login novo substitui a sessão de outro navegador).
   */
  setSession(session: SdkSession): void {
    this.session = session;
    this.setState({ rejected: null, sessionLost: null });
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      if (this.state.tabId) {
        this.send({ type: 'session', session });
        return;
      }
      // hello em voo, ainda sem welcome: a resposta pode ser a recusa daquele hello (que apagaria a
      // sessão nova). Descarta este socket e reconecta já apresentando a sessão no hello.
      const stale = this.ws;
      this.ws = null;
      stale.onopen = null;
      stale.onmessage = null;
      stale.onclose = null;
      stale.close();
      this.setState({ wsState: 'closed' });
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.reconnectDelay = RECONNECT_MIN_MS;
    this.connect();
  }

  engage(): void {
    this.send({ type: 'engage' });
  }

  release(): void {
    this.send({ type: 'release' });
  }

  command(command: Command): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN && this.state.tabId) {
      this.send({ type: 'command', command });
      this.log(`comando: ${command.name}${command.phone ? ` ${command.phone}` : ''}`);
      return;
    }
    // conexão caída ou reconectando (rede instável): sem isto o clique se perdia em silêncio e, por
    // exemplo, "encerrar" deixava a chamada ativa. O comando espera o welcome da reconexão.
    this.pendingCommands.push({ command, at: Date.now() });
    if (this.pendingCommands.length > PENDING_COMMANDS_MAX) this.pendingCommands.shift();
    this.log(`comando: ${command.name} guardado até a conexão com o motor voltar`);
    this.setState({ lastError: 'Sem conexão com o motor neste momento; o comando será enviado assim que ela voltar.' });
    this.connect();
  }

  /** manda os comandos guardados que ainda fazem sentido (os velhos poderiam atingir outra chamada) */
  private flushPendingCommands(): void {
    const now = Date.now();
    const pending = this.pendingCommands.splice(0);
    for (const entry of pending) {
      if (now - entry.at > PENDING_COMMANDS_TTL_MS) {
        this.log(`comando ${entry.command.name} descartado: a conexão demorou demais para voltar`);
        continue;
      }
      this.send({ type: 'command', command: entry.command });
      this.log(`comando: ${entry.command.name} (enviado depois da reconexão)`);
    }
  }

  // ------------------------------------------------------------------ áudio

  setInputMuted(muted: boolean): void {
    if (this.micStream) for (const track of this.micStream.getAudioTracks()) track.enabled = !muted;
    this.setState({ inputMuted: muted });
    // o motor também silencia o microfone virtual, para o estado valer em todas as abas
    if (this.currentCall) this.command({ name: muted ? 'mute' : 'unmute' });
  }

  setOutputMuted(muted: boolean): void {
    if (this.audio) this.audio.muted = muted;
    this.setState({ outputMuted: muted });
  }

  setOutputVolume(volume: number): void {
    const clamped = Math.max(0, Math.min(100, Math.round(volume)));
    if (this.audio) this.audio.volume = clamped / 100;
    this.setState({ outputVolume: clamped });
  }

  async setOutputDevice(deviceId: string): Promise<void> {
    const audio = this.audio as (HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> }) | null;
    if (!audio?.setSinkId) throw new Error('este navegador não permite escolher a saída de som');
    await audio.setSinkId(deviceId);
  }

  /** depois de um toque do agente: tenta tocar o som do motor que o navegador segurou */
  async unlockAudio(): Promise<void> {
    if (!this.audio || !this.audio.srcObject) return;
    try {
      await this.audio.play();
      this.setState({ needsUnlock: false });
    } catch {
      this.setState({ needsUnlock: true });
    }
  }

  private async startAudio(): Promise<void> {
    const seq = ++this.audioSeq;
    this.stopAudio();
    const mediaDevices = this.options.mediaDevices === undefined ? (typeof navigator !== 'undefined' ? navigator.mediaDevices : null) : this.options.mediaDevices;
    if (!mediaDevices?.getUserMedia) {
      this.setState({ micError: 'Este navegador não permite usar o microfone pela página.' });
      return;
    }
    let stream: MediaStream;
    try {
      stream = await mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
    } catch (error) {
      const message = `Microfone indisponível: ${(error as Error).message}`;
      this.setState({ micError: message });
      this.log(message);
      return;
    }
    if (seq !== this.audioSeq || !this.state.engaged) {
      for (const track of stream.getTracks()) track.stop();
      return;
    }
    this.micStream = stream;
    for (const track of stream.getAudioTracks()) track.enabled = !this.state.inputMuted;
    this.setState({ micError: null });

    const Impl = this.options.PeerConnectionImpl ?? RTCPeerConnection;
    const connection = new Impl({ iceServers: this.iceServers });
    this.pc = connection;
    const connectionId = Math.random().toString(36).slice(2, 10);
    this.connectionId = connectionId;
    this.remoteSet = false;
    this.pendingCandidates = [];
    for (const track of stream.getAudioTracks()) connection.addTrack(track, stream);
    connection.onicecandidate = (event) => {
      this.send({ type: 'signal', payload: { connectionId, candidate: event.candidate ? event.candidate.toJSON() : null } });
    };
    connection.ontrack = (event) => {
      const remote = event.streams[0] ?? new MediaStream([event.track]);
      if (this.audio) {
        this.audio.srcObject = remote;
        this.audio.muted = this.state.outputMuted;
        this.audio.volume = this.state.outputVolume / 100;
        this.audio
          .play()
          .then(() => this.setState({ needsUnlock: false }))
          .catch(() => {
            this.setState({ needsUnlock: true });
            this.armUnlock();
          });
      }
      this.log('áudio do motor chegou');
    };
    connection.onconnectionstatechange = () => {
      if (this.pc !== connection) return;
      this.setState({ pcState: connection.connectionState });
      this.log(`WebRTC: ${connection.connectionState}`);
      if (connection.connectionState === 'failed' && this.state.engaged) {
        this.log('conexão de áudio com o motor falhou; renegociando');
        void this.startAudio();
      }
    };
    const offer = await connection.createOffer();
    if (this.pc !== connection) return;
    await connection.setLocalDescription(offer);
    this.send({ type: 'signal', payload: { connectionId, description: { type: offer.type, sdp: offer.sdp } } });
    this.setState({ pcState: connection.connectionState });
  }

  private stopAudio(): void {
    if (this.pc) {
      this.pc.onconnectionstatechange = null;
      this.pc.onicecandidate = null;
      this.pc.ontrack = null;
      this.pc.close();
      this.pc = null;
    }
    this.connectionId = null;
    if (this.micStream) {
      for (const track of this.micStream.getTracks()) track.stop();
      this.micStream = null;
    }
    if (this.audio) this.audio.srcObject = null;
    if (this.state.pcState !== 'idle') this.setState({ pcState: 'idle' });
  }

  private async onSignal(payload: SignalPayload): Promise<void> {
    const connection = this.pc;
    if (!connection) return;
    if (payload.connectionId && payload.connectionId !== this.connectionId) return; // conexão antiga
    try {
      if (payload.description) {
        await connection.setRemoteDescription(payload.description);
        this.remoteSet = true;
        for (const candidate of this.pendingCandidates.splice(0)) await connection.addIceCandidate(candidate).catch(() => undefined);
      }
      if (payload.candidate !== undefined) {
        const candidate = payload.candidate ?? undefined;
        if (!this.remoteSet && candidate) this.pendingCandidates.push(candidate);
        else await connection.addIceCandidate(candidate).catch(() => undefined);
      }
    } catch (error) {
      this.log(`sinalização: ${(error as Error).message}`);
    }
  }

  /** o próximo toque/tecla na página libera o som (política de autoplay) */
  private armUnlock(): void {
    if (this.unlockListener || typeof document === 'undefined') return;
    const listener = () => {
      document.removeEventListener('pointerdown', listener, true);
      document.removeEventListener('keydown', listener, true);
      this.unlockListener = null;
      void this.unlockAudio();
    };
    this.unlockListener = listener;
    document.addEventListener('pointerdown', listener, true);
    document.addEventListener('keydown', listener, true);
  }

  // ------------------------------------------------------------------ transcrição

  private pushSegment(segment: TranscriptSegment): void {
    const current = this.state.transcript;
    let segments = current.segments;
    let insight = current.insight;
    let interims = current.interims;
    if (segments.length && segments[0].callId !== segment.callId) {
      segments = [];
      insight = null;
      interims = 0;
    }
    const index = segments.findIndex((s) => s.id === segment.id);
    const next = index >= 0 ? segments.map((s, i) => (i === index ? segment : s)) : [...segments, segment];
    if (next.length > MAX_SEGMENTS) next.shift();
    if (segment.interim) interims += 1;
    this.setState({ transcript: { ...current, segments: next, insight, interims } });
  }

  private onTranscriptStatus(status: TranscriptStatus): void {
    const current = this.state.transcript;
    if (!current.status || current.status.recordingId === status.recordingId || status.live === 'running') {
      const fresh = current.status && current.status.recordingId !== status.recordingId;
      this.setState({ transcript: { ...current, status, ...(fresh ? { segments: [], insight: null, interims: 0 } : {}) } });
    }
  }
}

function createAudioElement(): HTMLAudioElement | null {
  if (typeof document === 'undefined') return null;
  const audio = document.createElement('audio');
  audio.autoplay = true;
  audio.setAttribute('playsinline', 'true');
  audio.style.display = 'none';
  audio.dataset.callixEngineAudio = 'true';
  document.body.appendChild(audio);
  return audio;
}
